<template>
  <div class="plot-edit">
    <div class="plot-top-area">
      <input-text
        v-if="plotId"
        class="input plot-name-editing"
        :label="$t('plotEditPage.plotNamePlaceholder')"
        type="text"
        :maxlength="120"
        v-model="plotName"
        @input="input"
        @click.native="changePlotNameStatus"
        :class="{ 'plot-name': !changingPlotName }"
        v-click-outside="resetPlotNameStatus"
      />
      <div v-else class="all-plot">{{ $t("plotEditPage.overallPlot") }}</div>

      <div class="btn-mode-area">
        <select
          v-if="!isSP"
          class="selectbox filter-dropdown"
          :value="selectedLayout ? selectedLayout.id : ''"
          @change="onChangeLayout"
        >
          <option v-for="item in plotLayouts" :key="item.id" :value="item.id">
            {{ item.name }}
          </option>
        </select>
        <div v-if="!isSP" class="selectbox mode-box" @click="modeClick">
          <div class="selected">
            <div class="mode-text select-box-item">
              <div class="name">{{ $t("plotEditPage.modeLabel", { modeName }) }}</div>
            </div>
            <ChevronDownIcon class="caret" :fillColor="iconColor" :class="{ open: true }" />
          </div>
        </div>
        <button :disabled="!changed || plotName === ''" @click="save(false)" class="button save-button">
          {{ $t("common.save") }}
        </button>
      </div>
    </div>
    <div class="plot-groups">
      <draggable
        v-model="plotGroups"
        group="group"
        draggable=".draggable-group"
        handle=".handle"
        :animation="300"
        ghostClass="ghost-group"
        @start="onGroupDragStart"
        @end="onGroupDragEnd"
      >
        <transition-group name="fade-in" class="draggable">
          <div v-for="(plotGroup, index) in plotGroups" :key="`plot-group-${index}`" class="draggable-group">
            <div class="plot-group-header">
              <div class="plot-group-name" :class="{ 'plot-group-handle': isCustom }">
                <div class="plot-group-name-row">
                  <div class="plot-group-name-row-first">
                    <textarea
                      v-if="isCustom"
                      type="text"
                      v-model="plotGroup.name"
                      @input="input"
                      :placeholder="$t('plotEditPage.plotGroupNamePlaceholder')"
                    />
                    <div v-else>- {{ plotGroup.name }} -</div>
                  </div>
                  <div class="menu-parent" v-if="isCustom">
                    <content-duplicate-icon class="icon mr" @click="onClickContentCopy(index)" />
                    <plus-circle-outline-icon class="icon mr" @click="addPlotGroup(index)" />
                    <dots-horizontal-circle-icon class="icon light-gray" @click="clickDisplayMenu(index)" />
                    <!-- TODO  onClickOutsideがちゃんと効いていない-->
                    <div
                      v-if="displayMenu === index"
                      class="menu"
                      v-click-outside="onClickOutside"
                      @click="deletePlotGroup(index)"
                    >
                      {{ $t("plotEditPage.deletePlotGroup") }}
                    </div>
                  </div>
                </div>
                <textarea
                  class="textarea-theme"
                  :placeholder="$t('plotEditPage.plotGroupContentPlaceholder')"
                  v-model="plotGroup.description"
                  :maxlength="maxDescriptionLength"
                  @input="input"
                />
              </div>
              <drag-horizontal-variant-icon class="icon handle" v-if="isCustom" />
            </div>
            <div class="plots" v-show="!groupDragging">
              <draggable
                v-model="plotGroup.plots"
                group="item"
                draggable=".draggable-item"
                handle=".handle-disp"
                :animation="300"
                ghostClass="ghost"
                @end="onDragEnd"
              >
                <transition-group name="fade-in" class="draggable" v-if="!isDetail">
                  <plot-item-card
                    v-for="plot in plotGroup.plots"
                    :key="plot.key"
                    v-model="plot.text"
                    :isInitial="isInitial(plot)"
                    @input="inputItem(plot, plotGroup.plots)"
                    @deleteItem="deleteItem(plot, index)"
                    :class="{ 'draggable-item': !isInitial(plot) }"
                  />
                </transition-group>
                <transition-group name="fade-in" class="draggable-detail" v-else>
                  <plot-item-detail
                    v-for="(plot, j) in plotGroup.plots"
                    :key="plot.key"
                    v-model="plot.text"
                    :novelId="novelId"
                    :plotId="plotId"
                    :isInitial="isInitial(plot)"
                    :child="plot.linkedPlotKey"
                    @input="inputItem(plot, plotGroup.plots)"
                    @deleteItem="deleteItem(plot, index)"
                    @linkPlot="linkPlot($event, plot, index, j)"
                    @cancelLink="cancelLink(plot, index, j)"
                    :class="{ 'draggable-item': !isInitial(plot) }"
                  />
                </transition-group>
              </draggable>
            </div>
          </div>
        </transition-group>
      </draggable>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import PlotItemCard from "@/components/atoms/PlotItemCard.vue";
import PlotItemDetail from "@/components/atoms/PlotItemDetail.vue";
import InputText from "@/components/atoms/InputText.vue";
import isMobile from "ismobilejs";
import { Dialog } from "@/lib/utils";
import BreakingChangeConfirm, { BreakingChangeConfirmProps } from "@/components/ui/dialogs/BreakingChangeConfirm.vue";
import { plotLayouts, PlotLayoutSelectBoxItem, plotModes, PlotModeSelectBoxItem } from "@/lib/plot";

import draggable from "vuedraggable";
import {
  Layout,
  Mode,
  Plot,
  PlotGroups,
  Plots,
  SubPlot,
  CreateSubPlotPayload,
  UpdatePlotPayload,
  UpdateSubPlotPayload,
  DisplayFormat,
} from "@/lib/models/plot";
import { v4 as uuidv4 } from "uuid";
import ChevronDownIcon from "icons/ChevronDown.vue";
import PlusCircleOutlineIcon from "icons/PlusCircleOutline.vue";
import ContentDuplicateIcon from "icons/ContentDuplicate.vue";
import DotsHorizontalCircleIcon from "icons/DotsHorizontalCircle.vue";
import DragHorizontalVariantIcon from "icons/DragHorizontalVariant.vue";
import clone from "lodash/cloneDeep";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";

const ClickOutside = require("vue-click-outside");

export default Vue.extend<Data, Methods, Computed, Props>({
  directives: { ClickOutside },
  components: {
    InputText,
    PlotItemCard,
    PlotItemDetail,
    draggable,
    ChevronDownIcon,
    PlusCircleOutlineIcon,
    ContentDuplicateIcon,
    DotsHorizontalCircleIcon,
    DragHorizontalVariantIcon,
  },
  props: {
    novelId: String,
    plotId: String,
    plots: Object as PropType<Plot | SubPlot>,
  },
  data() {
    const { plotId } = this;
    const { plotGroups } = this.plots;

    return {
      plotGroups,
      plotName: plotId ? (this.plots as SubPlot).name : null,
      maxDescriptionLength: 2500,
      changed: false,
      changingPlotName: false,
      displayMenu: -1,
      plotLayouts,
      plotModes,
      selectedLayout: plotLayouts.find((i) => i.id === this.plots.layout) || plotLayouts[0],
      groupDragging: false,
      unbindShortcutKeys: null,
      themeWatcher: null as MutationObserver | null, // テーマ変更検知用
    };
  },
  created() {
    // 保存ボタンのショートカットキーの有効化と解除関数の取得
    this.unbindShortcutKeys = handleShortcutKeys(
      [
        { trigger: TriggerKey.Meta, keys: ["s"], callback: this.save },
        { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.save },
      ],
      false
    );
    // テーマ変更を検知するMutationObserverを設定
    if (typeof window !== "undefined") {
      this.themeWatcher = new MutationObserver(() => {
        // テーマ変更時にcomputed propertyを再計算させるため、$forceUpdateを呼び出す
        this.$forceUpdate();
      });
      this.themeWatcher.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-app-theme"],
      });
    }
  },
  beforeDestroy() {
    // コンポーネントが破棄される前に保存ボタンのショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
    // MutationObserverをクリーンアップ
    if (this.themeWatcher) {
      this.themeWatcher.disconnect();
      this.themeWatcher = null;
    }
  },
  computed: {
    mode() {
      const { plots } = this;
      return plots.mode || Mode.kishoutenketsu;
    },
    modeName() {
      const { mode } = this;
      return plotModes.find((i) => i.id === mode)!.name;
    },
    isCustom() {
      const { mode } = this;
      return mode === Mode.custom;
    },
    isSP() {
      return isMobile().any;
    },
    isDetail() {
      if (isMobile().any) {
        return true;
      }
      const { selectedLayout } = this;
      return selectedLayout.id === Layout.detail;
    },
    accentColor() {
      // CSS変数からアクセント色を取得
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue("--color-primary").trim() || "#76a4d3";
      }
      return "#76a4d3";
    },
    iconColor() {
      // 詳細設計の矢印と同じ色（グレー）を取得
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        return computedStyle.getPropertyValue("--color-text-low").trim() || "#9b9b9b";
      }
      return "#9b9b9b";
    },
  },
  methods: {
    isInitial(plot) {
      const extendedPlot = plot as ExtensionPlot;
      return extendedPlot.isInitial !== undefined ? extendedPlot.isInitial : false;
    },
    setChanged(b) {
      this.changed = b;
      this.setModified(b);
    },
    input() {
      this.setChanged(true);
    },
    async inputItem(plot, plots) {
      const { generateInitialPlot } = this;
      const { isInitial } = plot;

      if (isInitial) {
        const updatedPlot = { ...plot };
        delete updatedPlot.isInitial;
        plots.splice(plots.length - 1, 1, updatedPlot);
        plots.push(generateInitialPlot());
      }

      this.input();
    },
    deleteItem(plot, index) {
      this.plotGroups[index].plots = this.plotGroups[index].plots.filter((x) => x.key !== plot.key);

      this.input();
    },
    onGroupDragStart() {
      this.groupDragging = true;
    },
    onGroupDragEnd() {
      this.groupDragging = false;
      this.setChanged(true);
    },
    onDragEnd() {
      this.setChanged(true);
    },
    addPlotGroup(index) {
      const { $notify, setChanged } = this;

      const plotGroup = {
        name: "",
        description: "",
        plots: [],
      };
      this.plotGroups.splice(index + 1, 0, plotGroup);

      $notify({
        title: this.$t('plotEditPage.plotGroupAdded'),
        type: "success",
      });
      setChanged(true);
    },
    clickDisplayMenu(index) {
      if (this.displayMenu === index) {
        this.displayMenu = -1;
      } else {
        this.displayMenu = index;
      }
    },
    // TODO outside clickがちゃんと動いていない💦
    onClickOutside() {
      // this.displayMenu = -1;
    },
    linkPlot(data, plot, index, j) {
      // 新規作成の場合
      if (data.plotId === "new") {
        const { novelId, $router } = this;
        const mode = data.mode.id;
        const layout = Layout.simple;
        const displayFormat = DisplayFormat.HORIZONTAL;

        let plotGroups: PlotGroups[] = [];

        switch (mode) {
          default:
          case Mode.kishoutenketsu:
            plotGroups = [
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ki') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.shou') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ten') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.kishoutenketsu.ketsu') as string, description: "", plots: [] },
            ];
            break;
          case Mode.johakyu:
            plotGroups = [
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.jo') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.ha') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.johakyu.kyu') as string, description: "", plots: [] },
            ];
            break;
          case Mode.custom:
            plotGroups = [
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter1') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter2') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter3') as string, description: "", plots: [] },
              { name: this.$t('plotPage.plotCreateDialog.plotGroups.custom.chapter4') as string, description: "", plots: [] },
            ];
            break;
        }

        const createSubPlotPayload: CreateSubPlotPayload = {
          novelId,
          name: data.name,
          plotGroups,
          mode,
          layout,
          displayFormat,
          callback: async (plotId: string) => {
            const plotGroup = this.plotGroups[index];
            plotGroup.plots[j].linkedPlotKey = plotId;
            this.$set(this.plotGroups, index, plotGroup);
            // 移動するか確認ダイアログを表示する
            const confirmDialog = new Dialog(BreakingChangeConfirm);
            const data: BreakingChangeConfirmProps = {
              title: this.$t('plotEditPage.moveChildPlotTitle'),
              content: this.$t('plotEditPage.moveChildPlotContent'),
              change: this.$t('plotEditPage.moveButton'),
            };
            const result = await confirmDialog.show(data);

            if (!result) {
              return;
            }
            await this.save(true);
            $router.push({ name: "plotEdit", params: { novelId, plotId } });
          },
        };
        this.$store.dispatch("plotModule/createSubPlot", createSubPlotPayload);
      } else {
        // 既存と紐づける場合
        const plotGroup = this.plotGroups[index];
        plotGroup.plots[j].linkedPlotKey = data.plotId;
        this.$set(this.plotGroups, index, plotGroup);
      }
      this.setChanged(true);
    },
    cancelLink(plot, index, j) {
      const plotGroup = this.plotGroups[index];
      plotGroup.plots[j].linkedPlotKey = "";
      this.$set(this.plotGroups, index, plotGroup);
      this.setChanged(true);
    },
    async deletePlotGroup(index) {
      this.displayMenu = -1;
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t('plotEditPage.deletePlotGroupTitle'),
        content: this.$t('plotEditPage.deletePlotGroupContent'),
        change: this.$t('plotEditPage.deleteButton'),
      };
      const result = await confirmDialog.show(data);
      if (!result) {
        return;
      }
      const { $notify, setChanged } = this;

      this.plotGroups = this.plotGroups.filter((_, i) => i !== index);

      $notify({
        title: this.$t('plotEditPage.plotGroupDeleted'),
        type: "error",
      });
      setChanged(true);
    },
    generateInitialPlot() {
      const initialPlot: ExtensionPlot = {
        key: uuidv4(),
        text: "",
        isInitial: true,
        linkedPlotKey: "",
      };
      return initialPlot;
    },
    onChangeLayout(event: Event) {
      const target = event.target as HTMLSelectElement;
      const layoutId = target.value as Layout;
      const layout = plotLayouts.find((i) => i.id === layoutId);
      if (layout) {
        this.selectedLayout = layout;
        this.setChanged(true);
      }
    },
    modeClick() {
      const { $router, novelId, plotId, plots, mode } = this;

      if (plotId) {
        $router.push({
          name: "plotChange",
          params: { novelId, plotId },
          query: { mode: mode || "kishoutenketsu" },
        });
      } else {
        $router.push({
          name: "plotChange",
          params: { novelId },
          query: { mode: plots.mode || "kishoutenketsu" },
        });
      }
    },
    onClickContentCopy(index) {
      const { $notify, setChanged } = this;

      const newPlotGroup = clone(this.plotGroups[index]);
      // プロットのkeyはコピーせずに新規生成する
      newPlotGroup.plots = newPlotGroup.plots.map((plot) => {
        const newPlot = clone(plot);
        newPlot.key = uuidv4();
        return newPlot;
      });
      this.plotGroups.splice(index + 1, 0, newPlotGroup);

      $notify({
        title: this.$t('plotEditPage.plotGroupCopied'),
        type: "success",
      });
      setChanged(true);
    },
    changePlotNameStatus() {
      if (this.plotId) {
        this.changingPlotName = !this.changingPlotName;
      }
    },
    resetPlotNameStatus() {
      this.changingPlotName = false;
    },
    async save(isTransition) {
      if (!this.changed) return;

      const { $store, $router, $notify, novelId, plotId, mode, selectedLayout, plotName, setChanged } = this;
      let { plotGroups } = this;
      let { displayFormat } = this.plots;

      // remove initial plot
      plotGroups = plotGroups.map((plotGroup) => {
        const { name, description, plots } = plotGroup;
        return {
          name,
          description,
          plots: (plots as ExtensionPlot[]).filter((plot) => !plot.isInitial),
        };
      });

      // レイアウトを変更した場合はdisplayFormatも更新する
      if (selectedLayout.id !== this.plots.layout) {
        displayFormat = selectedLayout.id === Layout.detail ? DisplayFormat.VERTICAL : DisplayFormat.HORIZONTAL;
      }

      if (plotId && plotName) {
        const updateSubPlotPayload: UpdateSubPlotPayload = {
          novelId,
          plotId,
          name: plotName,
          plotGroups,
          mode,
          layout: selectedLayout.id,
          displayFormat,
        };
        $store.dispatch("plotModule/updateSubPlot", updateSubPlotPayload);
      } else {
        const updatePlotPayload: UpdatePlotPayload = {
          novelId,
          plotGroups,
          layout: selectedLayout.id,
          displayFormat,
        };
        $store.dispatch("plotModule/updatePlot", updatePlotPayload);
      }

      $notify({
        title: this.$t('plotEditPage.saved'),
        type: "success",
      });
      setChanged(false);

      if (!isTransition) {
        if (plotId) {
          $router.push({ name: "plot", params: { novelId, plotId } });
        } else {
          $router.push({ name: "plot", params: { novelId } });
        }
      }
    },
  },
  watch: {
    plotGroups: {
      handler() {
        const { plotGroups, generateInitialPlot } = this;

        plotGroups.forEach((plotGroup) => {
          // check if plotGroup has initial plot
          const hasInitialPlot = (plotGroup.plots as ExtensionPlot[]).some((plot) => plot.isInitial);
          if (!hasInitialPlot) {
            plotGroup.plots.push(generateInitialPlot());
          }

          // check if initial plot is last in the list
          const initialPlotIndex = (plotGroup.plots as ExtensionPlot[]).findIndex((plot) => plot.isInitial);
          if (plotGroup.plots.length - 1 !== initialPlotIndex) {
            const initialPlot = plotGroup.plots[initialPlotIndex];
            plotGroup.plots.splice(
              0,
              plotGroup.plots.length,
              ...plotGroup.plots.filter((_, index) => index !== initialPlotIndex)
            );
            plotGroup.plots.push(initialPlot);
          }
        });
      },
      deep: true,
      immediate: true,
    },
    selectedLayout() {
      this.setChanged(true);
    },
  },
});

interface Props {
  novelId: string;
  plotId?: string;
  plots: Plot | SubPlot;
}

interface Data {
  plotGroups: PlotGroups[];
  plotName: string | null;
  maxDescriptionLength: number;
  changed: boolean;
  changingPlotName: boolean;
  displayMenu: number;
  plotLayouts: PlotLayoutSelectBoxItem[];
  plotModes: PlotModeSelectBoxItem[];
  selectedLayout: PlotLayoutSelectBoxItem;
  groupDragging: boolean;
  unbindShortcutKeys: (() => void) | null;
  themeWatcher: MutationObserver | null;
}

interface Computed {
  mode: Mode;
  modeName: String;
  isCustom: boolean;
  isDetail: boolean;
  isSP: boolean;
  accentColor: string;
  iconColor: string;
}

interface Methods {
  isInitial(plot: Plots): boolean;
  setChanged(b: boolean): void;
  input(): void;
  inputItem(plot: ExtensionPlot, plots: ExtensionPlot[]): Promise<void>;
  deleteItem(plot: ExtensionPlot, index: number): void;
  onGroupDragStart(): void;
  onGroupDragEnd(): void;
  onDragEnd(): void;
  addPlotGroup(index: number): void;
  clickDisplayMenu(index: number): void;
  onClickOutside(): void;
  linkPlot(
    data: { plotId: string; name: string; mode: PlotModeSelectBoxItem },
    plot: ExtensionPlot,
    index: number,
    j: number
  ): void;
  cancelLink(plot: ExtensionPlot, index: number, j: number): void;
  deletePlotGroup(index: number): void;
  generateInitialPlot(): ExtensionPlot;
  save(isTransition: boolean): void;
  onChangeLayout(event: Event): void;
  modeClick(): void;
  onClickContentCopy(index: number): void;
  changePlotNameStatus(): void;
  resetPlotNameStatus(): void;
}

type ExtensionPlot = Plots & {
  isInitial?: boolean;
};
</script>

<style lang="scss">
@import "@/style-common.scss";

.plot-edit {
  .plot-top-area {
    padding: 0 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--color-surface-subtle);
    position: sticky;
    top: 0;
    z-index: 9;
    box-shadow: var(--shadow-sm);

    .btn-mode-area {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;
      margin-top: 0;
      margin-bottom: 0;

      .selectbox {
        width: auto;
        border-radius: var(--radius-xs);
        border: 1px solid var(--color-border);
        background-color: var(--color-background-normal);
        position: relative;
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
        cursor: pointer;

        &.filter-dropdown {
          padding: 10px 30px 10px 8px;
          color: var(--color-text-normal);
          cursor: pointer;
          transition: border-color 0.3s, background-color 0.3s;
          margin-right: 10px;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath fill='%23808080' d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
        }

        &.mode-box {
          margin-left: 10px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 5px 8px;
          color: var(--color-text-normal);
          transition: border-color 0.3s, background-color 0.3s;

          .selected {
            height: 100%;
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;

            .mode-text {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }

            .caret {
              display: flex;
              justify-content: center;
              align-items: center;
            }
          }
        }
      }

      .mode-text {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .save-button {
        border-radius: var(--radius-xs);
        background-color: var(--color-primary);
        color: var(--color-text-on-fixed-bg);
        font-weight: var(--font-weight-bold);
        padding: 10px 20px;
        border: none;
        cursor: pointer;
        transition: opacity 0.3s;

        &:hover:not(:disabled) {
          opacity: 0.9;
        }

        &:disabled {
          background-color: var(--color-surface-muted);
          color: var(--color-inactive);
          cursor: not-allowed;
        }
      }
    }
  }

  .plot-name-editing {
    border: 1px solid var(--color-border);
    box-sizing: border-box;
    flex-grow: 1;
    margin-right: 10px;
  }

  .all-plot {
    padding: 4px;
    font-size: 16.1px;
    line-height: 1.5;
    color: var(--color-text-normal);
  }

  .plot-name {
    position: relative;
    border: 1px solid transparent;
    background-color: transparent;
    box-sizing: border-box;
    cursor: pointer;
    &:hover {
      border: 1px solid var(--color-border);
    }
  }

  .icon {
    cursor: pointer;
    &.mr {
      margin-right: 10px;
    }
    svg {
      margin: 0 auto;
    }
    &.light-gray {
      svg {
        fill: var(--color-text-low);
      }
    }
  }

  .plot-groups {
    .plot-group-header {
      width: 100%;
      padding: 8px 15px 9px 20px;
      box-sizing: border-box;
      background: var(--color-surface-subtle);
      font-size: 1.5em;
      text-align: left;
      vertical-align: middle;
      color: var(--color-text-normal);
      font-weight: var(--font-weight-bold);
      position: relative;
      display: flex;
      align-items: center;

      .remove-button {
        position: absolute;
        top: 10px;
        right: 10px;
      }

      .plot-group-name {
        font-size: 16.1px;
        text-align: left;
        vertical-align: middle;
        color: var(--color-text-normal);
        font-weight: var(--font-weight-bold);
        display: flex;
        flex-direction: column;
        margin-right: 10px;
        flex-grow: 1;
        &.plot-group-handle {
          border-right: 3px solid var(--color-border);
          padding-right: 26px;
        }
        textarea {
          border: none;
          resize: none;
          border-radius: var(--radius-xs);
          flex-grow: 5;
          height: 24px;
          line-height: 24px;
          padding: 5px;
          background-color: var(--color-background-normal);
          color: var(--color-text-normal);
          &::placeholder {
            color: var(--color-text-low);
          }
        }
        .plot-group-name-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          .menu-parent {
            position: relative;
          }
          .menu {
            position: absolute;
            right: 0;
            top: 24px;
            width: 190px;
            font-size: var(--font-size-sm);
            color: var(--color-warning);
            border: 1px solid var(--color-border);
            padding: 5px;
            background: var(--color-background-normal);
            cursor: pointer;
          }
          .plot-group-name-row-first {
            width: 60%;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            textarea {
              flex-grow: 10;
            }
          }
        }
      }
      .textarea-theme {
        display: inline-block;
        vertical-align: middle;
        font-size: var(--font-size-md);
        width: 100% !important;
        height: auto;
        min-height: 50px;
        resize: none;
        outline: none;
        margin-top: 10px;
        background-color: var(--color-background-normal);
        color: var(--color-text-normal);
        border: 1px solid var(--color-border);
      }
      @include mq(sp) {
        padding: 10px 15px;
        border-left: var(--color-bg-dark) 0px solid;
        .textarea-theme {
          width: calc(100% - 10px);
          margin-left: 0;
          border-radius: var(--radius-xs);
          border: 1px solid var(--color-border);
        }
      }
    }

    .plots {
      .draggable {
        display: grid;
        grid-gap: 20px;
        padding: 20px 50px;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      }
    }

    .button-area {
      position: fixed;
      top: 60px;
      right: 10px;
      width: 155px;
      padding: 20px;
      z-index: 9999;

      @include mq(sp) {
        margin: 0px;
        top: auto;
        bottom: 50px;
        right: 0;
        left: 0;
        width: 100%;
        padding: 10px 0;
      }

      .button {
        width: 100%;
        height: 100%;
        border-radius: var(--radius-xs);
        background-color: var(--color-bg-dark);
        opacity: 0.9;
        color: var(--color-text-on-fixed-bg);

        &:disabled {
          background-color: var(--color-bg-dark);
          opacity: 0.5;
          color: var(--color-text-on-fixed-bg);

          @include mq(sp) {
            margin: 0px;
            border-radius: 0px;
          }
        }

        @include mq(sp) {
          margin: 0px;
          border-radius: 0px;
        }
      }
    }
  }
}

/** transition */
.fade-in-enter-active {
  transition: opacity 0.5s;
}
.fade-in-enter {
  opacity: 0;
}

/** vue-draggable用のスタイル */
.ghost-group,
.ghost {
  opacity: 0.5;
}
</style>
